/* Created by Interactive 63 - https://interactive63.com */
/**
 * Editor actions for shape morphing: converting shapes to paths, setting the shape at the
 * playhead from the artwork, pasted data or another file, and the start point alignment.
 */
import { convertShapeInSource, originalPathData } from '../../core/morph/convert';
import type { ElementId, MorphValue } from '../../core/model/types';
import { isMorphValue } from '../../core/model/types';
import { findKeyframe, findTrack, setKeyframe } from '../../core/timeline/keyframes';
import type { StagePanel } from '../panels/stage/stage';
import { setPropertyValue } from '../store/property-actions';
import { setKeyframeValue } from '../store/actions';
import { fitPathTo } from '../../core/morph/bounds';
import type { Store } from '../store/store';
import { toast } from '../ui/toast';
import { showContextMenu } from '../panels/timeline/context-menu';
import { fromFileDialog, pastePathDialog } from './shape-dialogs';

export class ShapeTools {
  /** Creates the tools. `time` supplies the playhead that shapes are set at. */
  constructor(
    private readonly store: Store,
    private readonly stage: StagePanel,
    private readonly time: () => number,
  ) {}

  /**
   * Makes sure an element is a path, which morphing needs. Other shapes are converted after the
   * person agrees, as one undoable change that leaves their appearance as it was.
   */
  ensurePath(elementId: ElementId): boolean {
    const { project } = this.store.getState();
    const meta = project.elements[elementId];
    if (!meta) return false;
    if (meta.tag === 'path') return true;
    const converted = convertShapeInSource(project.svg.source, elementId);
    if (!converted) return false;
    if (
      !window.confirm(
        `Convert ${meta.label} to a path so it can morph? Its appearance won't change.`,
      )
    ) {
      return false;
    }
    this.store.updateProject('Convert to path', (draft) => {
      draft.svg.source = converted.source;
      const element = draft.elements[elementId];
      if (element) {
        element.tag = 'path';
        // The stage measures the new path again.
        delete element.pathLength;
      }
    });
    return true;
  }

  /** The element's shape as stored, before any animation. */
  originalShape(elementId: ElementId): string {
    return originalPathData(this.store.getState().project.svg.source, elementId);
  }

  /**
   * Sets the element's shape at the playhead through the auto-key rules. The first shape on a
   * track also gets a keyframe at time zero holding the original, so the morph has a start.
   */
  setShape(
    elementId: ElementId,
    d: string,
    options: { hide?: ElementId; extras?: Omit<MorphValue, 'd'> } = {},
  ): void {
    const time = Math.round(this.time());
    const original = this.originalShape(elementId);
    this.store.transaction('Set shape', () => {
      const { project } = this.store.getState();
      if (!findTrack(project, elementId, 'morph') && time > 0 && original !== '') {
        this.store.updateProject('Add first shape', (draft) => {
          setKeyframe(draft, elementId, 'morph', 0, { d: original });
        });
      }
      const outcome = setPropertyValue(
        this.store,
        [elementId],
        'morph',
        { d, ...options.extras },
        { time },
      );
      if (outcome === 'rejected')
        toast.info('Auto-key is off. Click the diamond to add a keyframe.');
      if (options.hide) {
        const hide = options.hide;
        this.store.updateProject('Hide picked shape', (draft) => {
          const element = draft.elements[hide];
          if (element) {
            element.excludeFromExport = true;
            element.hiddenInEditor = true;
          }
        });
      }
    });
  }

  /** The menu for choosing where the new shape comes from. */
  menu(elementId: ElementId, anchor: HTMLElement): void {
    if (!this.ensurePath(elementId)) return;
    const box = anchor.getBoundingClientRect();
    showContextMenu(box.left, box.bottom + 2, [
      { label: 'Pick from artwork', onSelect: () => void this.pick(elementId) },
      { label: 'Paste path data…', onSelect: () => void this.paste(elementId) },
      { label: 'From another SVG file…', onSelect: () => void this.fromFile(elementId) },
    ]);
  }

  /** Picks a shape on the stage and morphs into it. */
  async pick(elementId: ElementId): Promise<void> {
    const picked = await this.stage.beginPick();
    if (!picked || picked.id === elementId) return;
    const d = this.placedPath(elementId, picked.id, picked.placement);
    if (d === null) {
      toast.warning('Pick a path or a basic shape. Groups cannot be morphed into.');
      return;
    }
    this.setShape(elementId, d, {
      ...(picked.hide ? { hide: picked.id } : {}),
      extras: { placement: picked.placement, sourceId: picked.id },
    });
  }

  /**
   * Another layer's shape for this element: where that layer is on the artboard, or fitted to
   * this element's own box. Null when it has no shape.
   */
  private placedPath(
    elementId: ElementId,
    sourceId: ElementId,
    placement: 'world' | 'in-place',
  ): string | null {
    const world = this.stage.pickedPath(elementId, sourceId);
    if (world === null || placement === 'world') return world;
    const own = this.originalShape(elementId);
    return own === '' ? world : fitPathTo(world, own);
  }

  /** Switches a picked shape between its place on the artboard and fitted to this layer. */
  setPlacement(keyframeId: string, placement: 'world' | 'in-place'): void {
    const found = findKeyframe(this.store.getState().project, keyframeId);
    const value = found?.keyframe.value;
    if (!found || !isMorphValue(value) || value.sourceId === undefined) return;
    if (!this.store.getState().project.elements[value.sourceId]) {
      toast.info('The layer this shape came from has been deleted, so it cannot be placed again.');
      return;
    }
    const d = this.placedPath(found.track.elementId, value.sourceId, placement);
    if (d === null) return;
    setKeyframeValue(this.store, keyframeId, { ...value, d, placement });
  }

  /** Asks for pasted path data and morphs into it. */
  async paste(elementId: ElementId): Promise<void> {
    const d = await pastePathDialog();
    if (d) this.setShape(elementId, d);
  }

  /** Chooses a shape from another SVG file and morphs into it. */
  async fromFile(elementId: ElementId): Promise<void> {
    const { project } = this.store.getState();
    const track = findTrack(project, elementId, 'morph');
    const current = track?.keyframes[0]?.value;
    const d = await fromFileDialog(
      isMorphValue(current) ? current.d : this.originalShape(elementId),
    );
    if (d) this.setShape(elementId, d);
  }

  /** Sets or clears the manual start point alignment of a morph keyframe. */
  setAlignment(keyframeId: string, align: MorphValue['align']): void {
    this.store.updateProject('Change start point', (draft) => {
      const found = findKeyframe(draft, keyframeId);
      if (found && isMorphValue(found.keyframe.value)) {
        // Pins, parts and placement stay; only the start point changes.
        const next: MorphValue = { ...found.keyframe.value };
        if (align) next.align = align;
        else delete next.align;
        found.keyframe.value = next;
      }
    });
  }
}
